// SpeechDetectionContext.tsx

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import type {
    SpeechDetectionStatus,
    SpeechDetectionContextState,
    SpeechDetectionProviderProps,
    AudioStats,
} from '@type/speechDetection'; // Assuming your path

// --- Configuration Defaults ---
const DEFAULT_VOLUME_THRESHOLD = 20;
const DEFAULT_SILENCE_DELAY = 500;

// Hysteresis + thresholds for audioStatus (ticket #1870).
//
// Upgrades to a better state are applied immediately. Downgrades only apply
// after the worse condition has been sustained for these durations, so brief
// pauses (e.g. listening to the AI between sentences) don't flip the in-interview
// "no microphone detected" tooltip on and off.
//
// Thresholds carve `percent` (0-100) into four buckets:
//   >= STRONG_PERCENT             -> 'strong'        (loud, clear speech)
//   [WEAK_PERCENT, STRONG_PERCENT) -> 'weak'         (genuine quiet speech)
//   [SILENCE_PERCENT, WEAK_PERCENT) -> hold previous (room/noise floor — ambiguous,
//                                                     don't flip the warning either way)
//   < SILENCE_PERCENT             -> 'low'           (no signal at all = dead mic)
//
// The middle "hold previous" bucket is what stops the in-interview "Low audio
// signal detected" popup from latching on whenever the room has any background
// hum: typical quiet rooms read in the 10-25 range, which used to fall into
// the old 'weak' bracket and stick there forever once the user stopped talking.
//
// HOLD_WEAK_MS is 2000ms (not 1500) to tolerate the natural 2-4 second
// thinking pauses common in interviews — at 1500ms, candidates pausing to
// formulate an answer were briefly tipping the warning on.
const HOLD_LOW_MS = 5000;
const HOLD_WEAK_MS = 2000;
const STRONG_PERCENT = 51;
const WEAK_PERCENT = 30;
const SILENCE_PERCENT = 8;

// Startup grace period: if we've been listening for STARTUP_GRACE_MS and have
// never observed percent >= STRONG_PERCENT, force a downgrade from the
// optimistic 'strong' default to 'weak'. Catches the silent-broken-mic case
// where a mic produces a constant mid-level signal (e.g. ~15% electrical
// noise) that sits in the 8-30 hold-previous bucket forever and would
// otherwise let a broken session through with no warning at all.
const STARTUP_GRACE_MS = 10000;

// Noise-floor detector constants (ticket #1871).
// If the user is NOT actively speaking (VAD silent) and the rolling average
// percent stays at or above NOISE_PERCENT for at least HOLD_NOISE_MS, we emit
// audioStatus === 'noise'. This fires only when the mic is alive and picking up
// sustained ambient noise — not when the user is talking and not when the mic
// is silent/dead. NOISE_PERCENT sits above SILENCE_PERCENT so a dead mic (< 8)
// never triggers noise, and below WEAK_PERCENT so real speech immediately
// cancels the noise state through the normal hysteresis path.
const NOISE_PERCENT = 25;
const HOLD_NOISE_MS = 3000;
// Rolling window (ms) over which we average audioLevel to smooth out brief pops.
const NOISE_WINDOW_MS = 2000;
// Cooldown after VAD transitions speaking -> silent before the noise detector
// resumes sampling. Browser AGC takes ~500-1000ms to ramp gain back down after
// speech ends, and during that window the elevated residual level looks
// identical to background noise — without this cooldown the 'noise' tooltip
// flashes on every time the candidate finishes a sentence.
const NOISE_SPEECH_COOLDOWN_MS = 1000;

// Empty stats — used for the default context value and as the shape returned
// by getAudioStats() when no session has run yet.
const emptyAudioStats = (): AudioStats => ({
    speakingHistogram: new Array(10).fill(0),
    silentHistogram: new Array(10).fill(0),
    speakingSamples: 0,
    silentSamples: 0,
    maxPercent: 0,
    transitionsLow: 0,
    transitionsWeak: 0,
    transitionsNoise: 0,
    transitionsStrong: 0,
    everSawStrong: false,
    sessionDurationMs: 0,
    micLabel: '',
});

// --- Context Definition ---
const defaultContextValue: SpeechDetectionContextState = {
    isTalking: false,
    speech_status: 'idle',
    error: null,
    startListening: async () => { },
    stopListening: () => { },
    audioLevel: 0,
    audioStatus: 'low',
    getAudioStats: emptyAudioStats,
};

const SpeechDetectionContext = createContext<SpeechDetectionContextState>(defaultContextValue);

// --- Provider Component ---
export const SpeechDetectionProvider: React.FC<SpeechDetectionProviderProps> = ({
    children,
    volumeThreshold = DEFAULT_VOLUME_THRESHOLD,
    silenceDelay = DEFAULT_SILENCE_DELAY,
    autoStart = true,
}) => {
    const [isTalking, setIsTalking] = useState<boolean>(false);
    const [speech_status, setSpeechStatus] = useState<SpeechDetectionStatus>('idle');
    const [error, setError] = useState<Error | null>(null);

    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const dataArrayRef = useRef<Uint8Array | null>(null);
    const rafRef = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isCurrentlyDetectingSpeechRef = useRef<boolean>(false);
    // Default to 'strong' (optimistic): assume the mic works until we observe
    // sustained silence. This stops the in-interview tooltip from flashing on
    // mount before the analyse loop has produced its first reading.
    const audioStatusRef = useRef<'low' | 'weak' | 'strong' | 'noise'>('strong');
    const audioLevelRef = useRef<number>(0);
    const [audioLevel, setAudioLevel] = useState<number>(0);
    const [audioStatus, setAudioStatus] = useState<'low' | 'weak' | 'strong' | 'noise'>('strong');
    const lastStateUpdateRef = useRef<number>(0);
    // Timestamps marking when the current sub-strong / sub-weak streak started.
    // Used to apply HOLD_WEAK_MS / HOLD_LOW_MS hysteresis on downgrades.
    const weakSinceRef = useRef<number | null>(null);
    const lowSinceRef = useRef<number | null>(null);

    // Noise-detector state (ticket #1871). Tracks a rolling window of samples
    // while the VAD reports no speech, then fires 'noise' if the average stays
    // above NOISE_PERCENT for HOLD_NOISE_MS.
    const noiseSamplesRef = useRef<{ t: number; v: number }[]>([]);
    const noiseSinceRef = useRef<number | null>(null);
    // Timestamp when VAD last transitioned from speaking to silent. null until
    // the first speech segment ends. Used to gate the noise detector with a
    // cooldown so AGC ramp-down isn't misclassified as ambient noise.
    const lastSpeechEndedAtRef = useRef<number | null>(null);

    // Startup grace state. `everStrongRef` flips true the first time the
    // analyse loop sees percent >= STRONG_PERCENT in the current session;
    // `listeningStartedAtRef` is the timestamp when the loop started running.
    // Together they let us downgrade the optimistic 'strong' default after
    // STARTUP_GRACE_MS if we never observed real speech-level signal.
    const everStrongRef = useRef<boolean>(false);
    const listeningStartedAtRef = useRef<number | null>(null);

    // Aggregate stats for Mixpanel telemetry — collected over the current
    // listening session, dumped via getAudioStats() at session end. Used to
    // validate the audio detection thresholds (#1870/#1871) against real
    // candidate hardware. Histograms have 10 bins covering 0-9%, 10-19%, ...,
    // 90-100% so we can build distributions of speaking vs silent levels.
    const audioStatsRef = useRef(emptyAudioStats());
    // Throttle histogram sampling to ~1Hz so long silent periods don't
    // dominate the distribution and so we cap memory work per session.
    const lastStatsSampleAtRef = useRef<number>(0);
    // Tracks the audioStatus from the previous frame so we can count
    // transitions when it changes.
    const prevAudioStatusForStatsRef = useRef<'low' | 'weak' | 'strong' | 'noise'>('strong');

    // Dev-only override. When non-null, the analyse loop writes this value
    // to audioStatus instead of its computed state. Set via
    // window.__forceAudioStatus in the browser console. Guarded by
    // import.meta.env.DEV so the whole code path is stripped from prod builds.
    const forcedAudioStatusRef = useRef<'low' | 'weak' | 'strong' | 'noise' | null>(null);

    // Ref to hold the latest speech_status for use in callbacks without causing re-creation
    const speechStatusRef = useRef<SpeechDetectionStatus>(speech_status);
    useEffect(() => {
        speechStatusRef.current = speech_status;
    }, [speech_status]);

    const analyse = useCallback(() => {
        if (
            !analyserRef.current ||
            !dataArrayRef.current ||
            !audioContextRef.current ||
            audioContextRef.current.state !== 'running'
        ) {
            return;
        }
        // @ts-ignore
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        let sum = 0;
        let max = 0;
        for (let i = 0; i < dataArrayRef.current.length; i++) {
            sum += dataArrayRef.current[i];
            if (dataArrayRef.current[i] > max) max = dataArrayRef.current[i];
        }
        const averageVolume = dataArrayRef.current.length > 0 ? sum / dataArrayRef.current.length : 0;
        // Normalize max to 0-100 percent (max possible value is 255)
        const percent = Math.round((max / 255) * 100);
        audioLevelRef.current = percent;

        const now = Date.now();
        // Hysteresis: upgrades apply immediately, downgrades require the bad
        // condition to be sustained. Four buckets — see threshold comment above.
        if (percent >= STRONG_PERCENT) {
            // Definitely strong speech — apply immediately, reset timers.
            // Also record that we've observed real strong-level signal in this
            // session so the startup grace check below leaves us alone. Only
            // this branch sets the flag — the dev override path intentionally
            // does NOT, so forced 'strong' from the console doesn't bypass the
            // grace check.
            audioStatusRef.current = 'strong';
            everStrongRef.current = true;
            weakSinceRef.current = null;
            lowSinceRef.current = null;
        } else if (percent >= WEAK_PERCENT) {
            // Quiet but real speech. Downgrade from strong only after sustained.
            lowSinceRef.current = null;
            if (audioStatusRef.current === 'strong') {
                if (weakSinceRef.current == null) weakSinceRef.current = now;
                if (now - weakSinceRef.current >= HOLD_WEAK_MS) {
                    audioStatusRef.current = 'weak';
                }
                // else: hold 'strong' a bit longer
            } else {
                // Coming from 'low' is an upgrade — apply 'weak' immediately.
                audioStatusRef.current = 'weak';
                weakSinceRef.current = null;
            }
        } else if (percent >= SILENCE_PERCENT) {
            // Ambiguous noise floor — neither speech nor dead mic. Hold the
            // previous status and clear both downgrade timers so a brief dip
            // through this bucket doesn't latch us into 'weak'.
            weakSinceRef.current = null;
            lowSinceRef.current = null;
        } else {
            // percent < SILENCE_PERCENT — no signal at all. Downgrade to 'low'
            // only after the silence has been sustained for HOLD_LOW_MS.
            weakSinceRef.current = null;
            if (lowSinceRef.current == null) lowSinceRef.current = now;
            if (now - lowSinceRef.current >= HOLD_LOW_MS) {
                audioStatusRef.current = 'low';
            }
            // else: hold previous status
        }

        // ── Noise detector (ticket #1871) ──────────────────────────────────
        // Only runs when the VAD says the user is NOT speaking AND the existing
        // hysteresis has not already decided to show 'low' or 'weak'. We do NOT
        // want 'noise' to mask a device-settings problem. We also wait
        // NOISE_SPEECH_COOLDOWN_MS after the most recent speech segment ends
        // so AGC ramp-down isn't misclassified as ambient noise.
        if (
            !isCurrentlyDetectingSpeechRef.current &&
            audioStatusRef.current !== 'low' &&
            audioStatusRef.current !== 'weak' &&
            (lastSpeechEndedAtRef.current == null ||
                now - lastSpeechEndedAtRef.current >= NOISE_SPEECH_COOLDOWN_MS)
        ) {
            // Maintain a rolling window of (timestamp, percent) samples.
            noiseSamplesRef.current.push({ t: now, v: percent });
            // Evict samples older than the window.
            noiseSamplesRef.current = noiseSamplesRef.current.filter(
                (s) => now - s.t <= NOISE_WINDOW_MS
            );
            const windowAvg =
                noiseSamplesRef.current.reduce((acc, s) => acc + s.v, 0) /
                (noiseSamplesRef.current.length || 1);

            if (windowAvg >= NOISE_PERCENT) {
                if (noiseSinceRef.current == null) noiseSinceRef.current = now;
                if (now - noiseSinceRef.current >= HOLD_NOISE_MS) {
                    audioStatusRef.current = 'noise';
                }
            } else {
                // Average dropped below threshold — cancel noise hold and clear
                // the 'noise' state so the status returns to 'strong'.
                noiseSinceRef.current = null;
                if (audioStatusRef.current === 'noise') {
                    audioStatusRef.current = 'strong';
                }
            }
        } else {
            // User is speaking or a device warning is active — clear noise state.
            noiseSamplesRef.current = [];
            noiseSinceRef.current = null;
            if (audioStatusRef.current === 'noise') {
                audioStatusRef.current = 'strong';
            }
        }

        // Startup grace: if we've been listening for STARTUP_GRACE_MS and have
        // never observed real strong-level signal, downgrade the optimistic
        // 'strong' default to 'weak' so a constant-mid-level broken mic
        // (e.g. ~15% electrical noise stuck in the hold-previous bucket)
        // doesn't silently pass through. One-shot — once downgraded, normal
        // hysteresis takes over from there.
        if (
            listeningStartedAtRef.current != null &&
            !everStrongRef.current &&
            audioStatusRef.current === 'strong' &&
            now - listeningStartedAtRef.current >= STARTUP_GRACE_MS
        ) {
            audioStatusRef.current = 'weak';
        }

        // Dev override — last writer wins, bypasses hysteresis entirely.
        // Stripped from prod builds via import.meta.env.DEV.
        if (import.meta.env.DEV && forcedAudioStatusRef.current !== null) {
            audioStatusRef.current = forcedAudioStatusRef.current;
        }

        // ── Aggregate stats for Mixpanel telemetry ─────────────────────────
        // Sample the histogram bin at ~1Hz (not every frame) so long silent
        // periods don't dominate the distribution. Transitions are counted
        // every frame because they're naturally rate-limited (status only
        // changes when it actually changes).
        {
            const stats = audioStatsRef.current;
            if (percent > stats.maxPercent) stats.maxPercent = percent;

            // Histogram sampling at ~1Hz, gated on listening having started
            // so we don't accumulate stats during initialization frames.
            if (
                listeningStartedAtRef.current != null &&
                now - lastStatsSampleAtRef.current >= 1000
            ) {
                lastStatsSampleAtRef.current = now;
                const bin = Math.min(9, Math.floor(percent / 10));
                if (isCurrentlyDetectingSpeechRef.current) {
                    stats.speakingHistogram[bin]++;
                    stats.speakingSamples++;
                } else {
                    stats.silentHistogram[bin]++;
                    stats.silentSamples++;
                }
            }

            // Transition counting — fires once per status change.
            if (audioStatusRef.current !== prevAudioStatusForStatsRef.current) {
                switch (audioStatusRef.current) {
                    case 'low': stats.transitionsLow++; break;
                    case 'weak': stats.transitionsWeak++; break;
                    case 'noise': stats.transitionsNoise++; break;
                    case 'strong': stats.transitionsStrong++; break;
                }
                prevAudioStatusForStatsRef.current = audioStatusRef.current;
            }
        }

        // Update audio level state every frame (16ms) for smooth bar animation
        // Only skip if value hasn't changed to avoid unnecessary re-renders
        if (now - lastStateUpdateRef.current > 16) {
            lastStateUpdateRef.current = now;
            setAudioLevel(percent);
            setAudioStatus(audioStatusRef.current);
        }

        if (averageVolume > volumeThreshold) {
            if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
                silenceTimeoutRef.current = null;
            }
            if (!isCurrentlyDetectingSpeechRef.current) {
                setIsTalking(true);
                isCurrentlyDetectingSpeechRef.current = true;
            }
        } else {
            if (isCurrentlyDetectingSpeechRef.current && !silenceTimeoutRef.current) {
                silenceTimeoutRef.current = setTimeout(() => {
                    setIsTalking(false);
                    isCurrentlyDetectingSpeechRef.current = false;
                    // Mark when speech actually transitioned to silent so the
                    // noise detector cooldown can suppress AGC ramp-down false
                    // positives. Set here (not when level drops below
                    // volumeThreshold) so we use the same debounced timing the
                    // VAD already commits to.
                    lastSpeechEndedAtRef.current = Date.now();
                    silenceTimeoutRef.current = null;
                }, silenceDelay);
            }
        }
        rafRef.current = requestAnimationFrame(analyse);
    }, [volumeThreshold, silenceDelay]);

    const stopListeningAndCleanup = useCallback(() => {
        // Use speechStatusRef for the check to make this callback stable
        if (speechStatusRef.current === 'stopping' || speechStatusRef.current === 'idle') {
            console.log("Cleanup check: Already stopping or idle. Current ref status:", speechStatusRef.current, "Actual state:", speech_status);
            return;
        }
        console.log("stopListeningAndCleanup: Cleaning up audio resources. Current status was:", speechStatusRef.current);
        setSpeechStatus('stopping');

        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
        weakSinceRef.current = null;
        lowSinceRef.current = null;
        noiseSamplesRef.current = [];
        noiseSinceRef.current = null;
        lastSpeechEndedAtRef.current = null;
        everStrongRef.current = false;
        listeningStartedAtRef.current = null;
        audioStatsRef.current = emptyAudioStats();
        lastStatsSampleAtRef.current = 0;
        prevAudioStatusForStatsRef.current = 'strong';
        sourceRef.current?.disconnect();
        sourceRef.current = null;
        analyserRef.current = null;
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;

        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close()
                .catch(e => console.warn("Error closing AudioContext:", e))
                .finally(() => {
                    audioContextRef.current = null;
                    // Only set to idle if we were indeed stopping, to prevent race conditions
                    if (speechStatusRef.current === 'stopping') { // Check ref again
                        setSpeechStatus('idle');
                    }
                    setIsTalking(false);
                    isCurrentlyDetectingSpeechRef.current = false;
                });
        } else {
            audioContextRef.current = null;
            if ((speechStatusRef.current as string) === 'stopping') { // Check ref again
                setSpeechStatus('idle');
            }
            setIsTalking(false);
            isCurrentlyDetectingSpeechRef.current = false;
        }
    }, []); // Empty dependency array makes this callback stable

    const startListening = useCallback(async () => {
        // Use speechStatusRef for the check to make this callback stable
        if (speechStatusRef.current === 'listening' || speechStatusRef.current === 'initializing') {
            console.warn("startListening: Already active or initializing. Ref status:", speechStatusRef.current, "Actual state:", speech_status);
            return;
        }

        // Clean up any existing resources before starting.
        // This is important if startListening can be called multiple times.
        if (audioContextRef.current || streamRef.current) {
            console.warn("startListening: Existing audio resources found. Calling stopListeningAndCleanup first.");
            // `stopListeningAndCleanup` is stable and will eventually set status to 'idle'.
            // This might cause a flicker if startListening is called again too quickly by an effect.
            // For now, this direct call is simpler.
            stopListeningAndCleanup();
            // Wait a tick for state to potentially update from stopListeningAndCleanup's async parts
            await new Promise(resolve => setTimeout(resolve, 0));
            if (speechStatusRef.current !== 'idle') {
                console.warn("startListening: Cleanup didn't result in 'idle' status quickly. Aborting start. Ref status:", speechStatusRef.current);
                return; // Abort if status isn't idle after cleanup attempt
            }
        }

        console.log("startListening: Initializing audio setup...");
        setSpeechStatus('initializing');
        setError(null);
        setIsTalking(false);
        isCurrentlyDetectingSpeechRef.current = false;
        weakSinceRef.current = null;
        lowSinceRef.current = null;
        noiseSamplesRef.current = [];
        noiseSinceRef.current = null;
        lastSpeechEndedAtRef.current = null;
        everStrongRef.current = false;
        listeningStartedAtRef.current = null;
        audioStatsRef.current = emptyAudioStats();
        lastStatsSampleAtRef.current = 0;
        prevAudioStatusForStatsRef.current = 'strong';

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            console.error("getUserMedia not supported");
            setSpeechStatus('nosupport');
            setError(new Error("Browser does not support microphone access."));
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // If status changed away from 'initializing' during await, it means stopListeningAndCleanup was called
            if ((speechStatusRef.current as string) !== 'initializing') {
                console.warn("startListening: Status changed during getUserMedia. Aborting. Ref status:", speechStatusRef.current);
                stream.getTracks().forEach(track => track.stop()); // Stop the newly acquired stream
                return;
            }
            streamRef.current = stream;

            // Capture the human-readable mic label for telemetry segmentation.
            // Read once at session start — labels don't change mid-stream, and
            // doing it here means getAudioStats() snapshots remain pure
            // accessors with no DOM/Media API touchpoints.
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length > 0) {
                audioStatsRef.current.micLabel = audioTracks[0].label || '';
            }

            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContextClass) {
                setSpeechStatus('nosupport');
                setError(new Error("Web Audio API not supported."));
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            const context = new AudioContextClass();
            if (context.state === 'suspended') await context.resume();
            if (context.state !== 'running') throw new Error(`AudioContext failed to start. State: ${context.state}`);
            audioContextRef.current = context;

            analyserRef.current = context.createAnalyser();
            analyserRef.current.fftSize = 512;
            dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
            sourceRef.current = context.createMediaStreamSource(stream);
            sourceRef.current.connect(analyserRef.current);

            console.log("startListening: Audio setup complete. Starting analysis loop.");
            if ((speechStatusRef.current as string) === 'initializing') { // Final check before starting loop
                setSpeechStatus('listening');
                // Mark the start of the analyse loop for the startup grace
                // check. Must come before the first RAF tick.
                listeningStartedAtRef.current = Date.now();
                rafRef.current = requestAnimationFrame(analyse);
            } else {
                console.warn("startListening: Status changed before RAF. Aborting loop. Ref status:", speechStatusRef.current);
                stream.getTracks().forEach(track => track.stop());
                context.close().catch(e => console.warn("Error closing context on aborted RAF:", e));
                sourceRef.current?.disconnect();
            }
        } catch (err: any) {
            console.error("Error during audio setup:", err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') setSpeechStatus('denied');
            else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') setSpeechStatus('error');
            else setSpeechStatus('error');
            setError(err instanceof Error ? err : new Error(String(err.message || "Unknown setup error.")));
            stopListeningAndCleanup(); // Ensure cleanup on error
        }
    }, [analyse]); // `analyse` is stable because its deps (volumeThreshold, silenceDelay) are props

    useEffect(() => {
        if (autoStart) {
            console.log("Provider Effect (autoStart): Calling startListening.");
            startListening();
        }
        // This cleanup runs when the provider unmounts OR if autoStart/startListening/stopListeningAndCleanup references change.
        // By making startListening and stopListeningAndCleanup stable, this primarily becomes an unmount cleanup.
        return () => {
            console.log("Provider Effect (autoStart): Cleanup. Calling stopListeningAndCleanup.");
            stopListeningAndCleanup();
        };
    }, [autoStart, startListening, stopListeningAndCleanup]); // Dependencies

    // Dev-only: expose a console helper for manual QA of the in-interview
    // audio warning tooltips (#1871). Browser AGC + the intentional 8-30%
    // hold-previous hysteresis bucket make it nearly impossible to trigger
    // 'weak' or 'noise' reliably from real audio, so we let testers force
    // any state from the DevTools console:
    //
    //   window.__forceAudioStatus('weak' | 'low' | 'noise' | 'strong' | null)
    //
    // Passing null releases the override and resumes real detection. The
    // entire effect is stripped from production builds by import.meta.env.DEV.
    useEffect(() => {
        if (!import.meta.env.DEV) return;
        type ForcedStatus = 'low' | 'weak' | 'strong' | 'noise' | null;
        const force = (v: ForcedStatus) => {
            forcedAudioStatusRef.current = v;
            if (v !== null) {
                audioStatusRef.current = v;
                setAudioStatus(v);
                // eslint-disable-next-line no-console
                console.log(`[SpeechDetection] audioStatus forced to "${v}"`);
            } else {
                // eslint-disable-next-line no-console
                console.log("[SpeechDetection] audioStatus override cleared");
            }
        };
        (window as unknown as { __forceAudioStatus?: (v: ForcedStatus) => void }).__forceAudioStatus = force;
        // Discoverability hint for non-engineer QA testers — engineers will
        // find the helper from code, but testers won't know to look.
        // eslint-disable-next-line no-console
        console.log(
            "[SpeechDetection] Dev mode: window.__forceAudioStatus('low'|'weak'|'strong'|'noise'|null) available for manual QA"
        );
        return () => {
            delete (window as unknown as { __forceAudioStatus?: (v: ForcedStatus) => void }).__forceAudioStatus;
        };
    }, []);

    // Snapshot accessor for telemetry. Returns a defensive copy so consumers
    // can serialize/log it without worrying about subsequent mutation by the
    // analyse loop. sessionDurationMs is computed at call time from the
    // listening start timestamp so callers always get a fresh value.
    const getAudioStats = useCallback((): AudioStats => {
        const s = audioStatsRef.current;
        const sessionDurationMs =
            listeningStartedAtRef.current != null
                ? Date.now() - listeningStartedAtRef.current
                : 0;
        return {
            speakingHistogram: [...s.speakingHistogram],
            silentHistogram: [...s.silentHistogram],
            speakingSamples: s.speakingSamples,
            silentSamples: s.silentSamples,
            maxPercent: s.maxPercent,
            transitionsLow: s.transitionsLow,
            transitionsWeak: s.transitionsWeak,
            transitionsNoise: s.transitionsNoise,
            transitionsStrong: s.transitionsStrong,
            everSawStrong: everStrongRef.current,
            sessionDurationMs,
            micLabel: s.micLabel,
        };
    }, []);

    const contextValue = useMemo((): SpeechDetectionContextState => ({
        isTalking,
        speech_status,
        error,
        startListening,
        stopListening: stopListeningAndCleanup,
        audioLevel,
        audioStatus,
        getAudioStats,
    }), [isTalking, speech_status, error, startListening, stopListeningAndCleanup, audioLevel, audioStatus, getAudioStats]);

    return (
        <SpeechDetectionContext.Provider value={contextValue}>
            {children}
        </SpeechDetectionContext.Provider>
    );
};

// --- Custom Hook --- (no change)
export const useSpeechDetection = (): SpeechDetectionContextState => {
    const context = useContext(SpeechDetectionContext);
    if (context === defaultContextValue && Object.keys(context).every(k => defaultContextValue[k as keyof SpeechDetectionContextState] === context[k as keyof SpeechDetectionContextState])) {
        console.warn('useSpeechDetection might be used outside a SpeechDetectionProvider if values are still default.');
    }
    return context;
};